// LevelCascade — THE cascade over catalog levels (L2 ▸ L3 ▸ row).
// Tier 2 (Component_Contract) — domain-aware: it groups a flat `rows` list into
// levels itself and formats the leaf's price, which CascadeSelect deliberately
// does not do. Built ON `CascadeSelect` (2026-08-21) rather than hand-assembling
// its own `CascadeDropdown`s — every OTHER multi-level-plus-leaf picker in the
// app (ComponentRefEditor+ComponentPickEditor, FuncPathDropdowns, CatalogReparent,
// RegisterForm) is CascadeSelect(levels) + one trailing leaf CascadeDropdown; this
// used to be the one exception, walking L2/L3/leaf with 3 separate CascadeDropdown
// calls instead of handing CascadeSelect the levels it already knows how to walk.
// Only the leaf's price-formatted `label` and the `collapseSingleLeaf` plain-text
// fallback (below) are genuinely this component's own job — the walking isn't.
//
// ⚠ THIS IS THE ONE PLACE THE CASCADE RULES LIVE. Do not hand-roll another
// cascade; add a prop here instead. Two hand-written copies (RM Source and
// FabLabour) is how the middle-level-collapse bug came to exist TWICE, and how
// the code-vs-label rule below got broken after being agreed.
//
// THE RULES (Alex, stated repeatedly; canonical in STATUS.md "Naming"):
//   1. Options are TWO COLUMNS: tag/code on the left, label on the right.
//   2. A CLOSED dropdown shows the TAG ONLY — never the label.
//   3. Only the LAST (leaf) level shows a label when closed; that is the level
//      that identifies the thing, so it is the one that has to read in words.
//   4. AUTO-ADVANCE: picking a level opens the next one. Not optional.
//   5. Nothing is written on pick — every change is STAGED, and the row's Save
//      commits it. A mis-pick must be undoable with Cancel.
//
// Props:
//   rows      — [{ id, l2, l3, l2_label, l3_label, label, price, unit }]
//               the selectable leaves, each carrying its level codes + labels.
//   value     — currently selected leaf id (or null/'')
//   onPick    — (id | '') => void. Called on every level change: '' when an
//               upper level changes (which invalidates the leaf), the id when a
//               leaf is chosen. The CONSUMER stages it; this component never writes.
//   labels    — { l2, l3, leaf } aria-labels, so the two uses stay distinguishable
//               to screen readers and tests.
//   widths    — optional { l2, l3, leaf } overrides; defaults suit a grid cell.
//
// State (drafts + which level to auto-open) is held HERE, per instance, so the
// consumer does not carry per-row maps. It resets naturally when the cell leaves
// edit mode and the component unmounts.

//   collapseSingleLeaf — when the chosen L2 ▸ L3 leaves exactly ONE row, do not
//               render the leaf dropdown: pick it, and show it as text instead.
//               Alex, 2026-07-31: "Family dropdown is not required because already
//               chosen." A dropdown with one option is not a choice, and drawing
//               it as one implies the user decided something they did not.
//               It stays a real DROPDOWN whenever two rows share an L2+L3 — which
//               is the only shape multi-source materials take today (both such
//               materials differ by ROW alone, one of them by 20% in price), so
//               collapsing unconditionally would have hidden a real decision and
//               silently mispriced it.

import { useEffect, useState } from 'react';
import CascadeSelect, { useCascadeSignal } from './CascadeSelect.jsx';
import { rate } from '../format.js';

export default function LevelCascade({ rows = [], value, onPick, labels = {}, widths = {}, collapseSingleLeaf = false }) {
  // Draft = the level the user has chosen but not yet completed down to a leaf.
  const [draft, setDraft] = useState({ l2: null, l3: null });
  // Auto-advance now comes from the shared hook rather than a private copy of the
  // same six lines (2026-08-04). Behaviour is identical -- this WAS the
  // undefined-until-targeted implementation the hook standardises on.
  const cascade = useCascadeSignal();

  const hit = rows.find(r => String(r.id) === String(value)) || null;

  // DRAFT WINS over the resolved row: picking a middle level clears the leaf, so
  // if `hit` took precedence the level above would fall back to nothing and the
  // whole cascade would collapse to empty options.
  const curL2 = draft.l2 ?? hit?.l2 ?? '';
  const inL2  = rows.filter(r => r.l2 === curL2);
  const curL3 = draft.l3 ?? hit?.l3 ?? '';
  const inL3  = inL2.filter(r => r.l3 === curL3);

  const uniq = (list, codeKey, labelKey) =>
    [...new Map(list.map(r => [r[codeKey], r[labelKey]])).entries()];

  const advance = cascade.advance;
  const signal  = cascade.signalFor;

  // The leaf collapses only when it is genuinely not a choice. Staging it here
  // rather than in the consumer keeps rule 5 intact: this still goes through
  // onPick, so it lands in the row buffer and Cancel undoes it like any pick.
  const soleLeaf = collapseSingleLeaf && curL3 && inL3.length === 1 ? inL3[0] : null;
  useEffect(() => {
    if (soleLeaf && String(value ?? '') !== String(soleLeaf.id)) onPick(String(soleLeaf.id));
  }, [soleLeaf?.id, value]);

  // Rule 1+2: code is the option's `code` (left column AND the closed button),
  // label is the right column only. No `display` override on L2/L3 — that is
  // exactly what broke the rule before. The leaf DOES use `display` (rule 3: it
  // reads in words when closed), same as every other leaf-pick in the app.
  const levels = [
    {
      ariaLabel: labels.l2 || 'Level 2', width: widths.l2 ?? 64, placeholder: '—',
      value: curL2,
      options: uniq(rows, 'l2', 'l2_label').map(([code, label]) => ({ code, label: label || code })),
      onChange: l2 => {
        setDraft({ l2, l3: null });
        onPick('');            // upper level changed → the leaf no longer applies
        advance('l3');         // rule 4
      },
    },
    // L3 — only once L2 is picked (an entry omitted from `levels` renders nothing,
    // same as the old `{curL2 ? <CascadeDropdown/> : null}`).
    ...(curL2 ? [{
      ariaLabel: labels.l3 || 'Level 3', width: widths.l3 ?? 64, placeholder: '—',
      value: curL3, openSignal: signal('l3'),
      options: uniq(inL2, 'l3', 'l3_label').map(([code, label]) => ({ code, label: label || code })),
      onChange: l3 => {
        // Pin the level above: clearing the leaf drops `hit`, so without this
        // the parent level would lose its seeded value.
        setDraft({ l2: curL2, l3 });
        onPick('');
        advance('leaf');
      },
    }] : []),
    // Leaf — rule 3: the LEAF is the level that reads in words, so it shows the
    // label (via `display`) rather than just the code. Omitted once `soleLeaf`
    // takes over (the plain-text fallback below) or before L3 is picked.
    ...(curL3 && !soleLeaf ? [{
      ariaLabel: labels.leaf || 'Item', width: widths.leaf ?? 170, placeholder: '— pick —',
      value: value ? String(value) : '', openSignal: signal('leaf'),
      options: inL3.map(r => ({
        code: String(r.id),
        display: r.label,
        label: r.price != null ? rate(r.price, r.unit) : '',
      })),
      onChange: id => onPick(id || ''),
    }] : []),
  ];

  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
      <CascadeSelect levels={levels} gap={3} />
      {soleLeaf && (
        // Not a dropdown, but rule 3 still applies: the last thing on the row is
        // what has to read in words, so the row and its rate stay on screen. The
        // control disappears; the ANSWER must not.
        <span style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
          {soleLeaf.label}{soleLeaf.price != null ? ` · ${rate(soleLeaf.price, soleLeaf.unit)}` : ''}
        </span>
      )}
    </div>
  );
}
